import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface RecipientFilters {
  audienceType: string;
  listType: string;
  status: string;
}

interface RecipientListFilterProps {
  audienceType: string;
  listType: string;
  status: string;
  audienceTypeList: Array<{ label: string; value: string }>;
  listTypeList: Array<{ label: string; value: string }>;
  statusList: Array<{ label: string; value: number }>;
  onApplyFilters: (filters: RecipientFilters) => void;
  onResetFilters: () => void;
}

export const RecipientListFilter: React.FC<RecipientListFilterProps> = ({
  audienceType,
  listType,
  status,
  audienceTypeList,
  listTypeList,
  statusList,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform lists to options format
  const audienceTypeOptions = useMemo(
    () =>
      audienceTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [audienceTypeList]
  );

  const listTypeOptions = useMemo(
    () =>
      listTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [listTypeList]
  );

  const statusOptions = useMemo(
    () =>
      statusList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [statusList]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "audienceType",
        label: "Audience Type",
        type: "select",
        placeholder: "Select Audience Type",
        options: audienceTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "listType",
        label: "List Type",
        type: "select",
        placeholder: "Select List Type",
        options: listTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: statusOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [audienceTypeOptions, listTypeOptions, statusOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      audienceType: decodeURIComponent(audienceType) || "",
      listType: decodeURIComponent(listType) || "",
      status: decodeURIComponent(status) || "",
    });
  }, [audienceType, listType, status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      audienceType: formValues.audienceType || "",
      listType: formValues.listType || "",
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      audienceType: "",
      listType: "",
      status: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
